// Matches a bare or `#`-prefixed 3- or 6-digit hex colour, nothing else. Deliberately narrower
// than Mantine's own `ColorInput` validity check, which also matches hexa/rgb/rgba/hsl/hsla - see
// `usePaletteColourDrafts`'s own comment for why that distinction matters.
const HEX_COLOUR_PATTERN = /^#?[0-9a-f]{3}$|^#?[0-9a-f]{6}$/i;

export function normaliseHexColour(value: string): string | null {
  const trimmed = value.trim();
  if (!HEX_COLOUR_PATTERN.test(trimmed)) {
    return null;
  }
  const digits = trimmed.startsWith('#') ? trimmed.slice(1) : trimmed;
  const sixDigitHex =
    digits.length === 3
      ? digits
          .split('')
          .map(digit => digit + digit)
          .join('')
      : digits;
  return `#${sixDigitHex.toLowerCase()}`;
}

function hexToRgbChannels(hex: string): [number, number, number] {
  const clean = normaliseHexColour(hex) ?? '#ffffff';
  return [
    parseInt(clean.slice(1, 3), 16),
    parseInt(clean.slice(3, 5), 16),
    parseInt(clean.slice(5, 7), 16),
  ];
}

function channelsToHex(r: number, g: number, b: number): string {
  const channel = (value: number) => Math.round(value).toString(16).padStart(2, '0');
  return `#${channel(r)}${channel(g)}${channel(b)}`;
}

// Matches `getLightTint` in docxModifier.ts (kept separate since that file is ported unchanged
// from the standalone app - see DECISIONS.md #6) - mixes 30% colour with 70% white so the preview
// shows the same tint Word ends up with, not the raw brand colour.
export function lightTint(hex: string): string {
  const [r, g, b] = hexToRgbChannels(hex);
  const mix = (c: number) => c * 0.3 + 255 * 0.7;
  return channelsToHex(mix(r), mix(g), mix(b));
}

// Matches `getContrastColor` in docxModifier.ts - same luma threshold, so a table header that
// reads as dark/light in the generated docx reads the same way in the preview.
export function contrastTextColour(backgroundHex: string, darkTextHex: string): string {
  const [r, g, b] = hexToRgbChannels(backgroundHex);
  const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luma >= 140 ? darkTextHex : '#ffffff';
}
